import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, apagarJson, atualizarJson, editarJson, enviarJson, pedirJson } from "./api";
import { esquemaCampoDoChamado, esquemaTicket } from "./tickets";

/* Núcleo avançado do chamado: campos personalizados, relações (pai/filho, relacionados, mescla,
   clone) e abertura programada. Fica à parte de `tickets.ts` para as frentes do atendimento não
   disputarem o mesmo arquivo; as chaves começam em `["tickets"]`, então tudo o que `tickets.ts`
   invalida (responder, alterar) atualiza estas telas também. */

const CHAVE = ["tickets"] as const;
const chaveDoChamado = (id: string) => [...CHAVE, id] as const;

/* ------------------------------------------------------------------------ campos */

export const TIPOS_DE_CAMPO = ["texto", "numero", "data", "lista", "multipla", "sim_nao"] as const;
export type TipoDeCampo = (typeof TIPOS_DE_CAMPO)[number];

export const ROTULO_DO_TIPO_DE_CAMPO: Record<TipoDeCampo, string> = {
  texto: "Texto",
  numero: "Número",
  data: "Data",
  lista: "Lista (uma opção)",
  multipla: "Múltipla escolha",
  sim_nao: "Sim ou não",
};

const esquemaCondicao = z.object({ campo_id: z.string().uuid(), valor: z.unknown() });

const esquemaDefinicaoDeCampo = z.object({
  id: z.string().uuid(),
  rotulo: z.string(),
  tipo: z.enum(TIPOS_DE_CAMPO),
  opcoes: z.array(z.string()),
  obrigatorio: z.boolean(),
  visivel_ao_cliente: z.boolean(),
  categoria: z.string().nullable(),
  servico: z.string().nullable(),
  condicao: esquemaCondicao.nullable(),
  ordem: z.number().int(),
  ativo: z.boolean(),
});

export type DefinicaoDeCampo = z.infer<typeof esquemaDefinicaoDeCampo>;
export type CondicaoDeCampo = z.infer<typeof esquemaCondicao>;

export interface PedidoDeCampo {
  rotulo: string;
  tipo: TipoDeCampo;
  opcoes: string[];
  obrigatorio: boolean;
  visivel_ao_cliente: boolean;
  categoria: string | null;
  servico: string | null;
  condicao: { campo_id: string; valor: unknown } | null;
  ordem?: number;
}

function normalizar(texto: string | null | undefined): string {
  return (texto ?? "").trim().toLocaleLowerCase("pt-BR");
}

/** Espelho de `campos.aplicaveis` da API: só para a tela mostrar e esconder campos enquanto a
    pessoa preenche. Quem decide de verdade é o servidor. */
export function camposAplicaveis<T extends { id: string; categoria: string | null; servico: string | null; condicao: CondicaoDeCampo | null }>(
  definicoes: readonly T[],
  categoria: string | null,
  servico: string | null,
  valores: Readonly<Record<string, unknown>>,
): T[] {
  const porId = new Map(definicoes.map((campo) => [campo.id, campo]));
  const memo = new Map<string, boolean>();

  const vale = (campo: T, visitados: ReadonlySet<string>): boolean => {
    const guardado = memo.get(campo.id);
    if (guardado !== undefined) return guardado;
    if (visitados.has(campo.id)) return false;
    let resultado =
      (!campo.categoria || normalizar(campo.categoria) === normalizar(categoria)) &&
      (!campo.servico || normalizar(campo.servico) === normalizar(servico));
    if (resultado && campo.condicao) {
      const outro = porId.get(campo.condicao.campo_id);
      const atual = outro ? valores[outro.id] : undefined;
      const esperado = campo.condicao.valor;
      resultado =
        outro !== undefined &&
        vale(outro, new Set([...visitados, campo.id])) &&
        (Array.isArray(atual) ? atual.includes(esperado) : atual !== null && atual !== undefined && atual === esperado);
    }
    memo.set(campo.id, resultado);
    return resultado;
  };
  return definicoes.filter((campo) => vale(campo, new Set()));
}

/** Lista vazia e texto em branco contam como não preenchido; `false` e `0` são respostas. */
export function valorPreenchido(valor: unknown): boolean {
  if (valor === null || valor === undefined) return false;
  if (typeof valor === "string") return valor.trim().length > 0;
  if (Array.isArray(valor)) return valor.length > 0;
  return true;
}

/** O valor como aparece para quem lê. */
export function textoDoValor(valor: unknown): string {
  if (!valorPreenchido(valor) && valor !== false && valor !== 0) return "—";
  if (valor === true) return "Sim";
  if (valor === false) return "Não";
  if (Array.isArray(valor)) return valor.join(", ");
  if (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [ano, mes, dia] = valor.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  return String(valor);
}

/** Na abertura o chamado ainda não tem categoria nem serviço: valem os campos sem escopo. */
export function camposDaAbertura(definicoes: readonly DefinicaoDeCampo[], valores: Record<string, unknown>) {
  return camposAplicaveis(
    definicoes.filter((campo) => campo.ativo),
    null,
    null,
    valores,
  );
}

function respondido(valor: unknown): boolean {
  return valorPreenchido(valor) || valor === false || valor === 0;
}

/** O que falta responder entre os campos obrigatórios que aparecem para a pessoa. */
export function pendentesDaAbertura(definicoes: readonly DefinicaoDeCampo[], valores: Record<string, unknown>) {
  return camposDaAbertura(definicoes, valores).filter((campo) => campo.obrigatorio && !respondido(valores[campo.id]));
}

/** Só o que aparece (e foi respondido) vai para a API: campo oculto por condição não segue junto. */
export function valoresParaEnviar(definicoes: readonly DefinicaoDeCampo[], valores: Record<string, unknown>) {
  return Object.fromEntries(
    camposDaAbertura(definicoes, valores)
      .filter((campo) => respondido(valores[campo.id]))
      .map((campo) => [campo.id, valores[campo.id]]),
  );
}

export function useDefinicoesDeCampo(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "campos"],
    queryFn: ({ signal }) => pedirJson("/tickets/campos", z.array(esquemaDefinicaoDeCampo), { signal }),
    enabled,
  });
}

export function useCriarCampo() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeCampo) => enviarJson("/tickets/campos", esquemaDefinicaoDeCampo, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "campos"] }),
  });
}

export function useAlterarCampo() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mudancas }: { id: string; mudancas: Partial<PedidoDeCampo> & { ativo?: boolean } }) =>
      editarJson(`/tickets/campos/${encodeURIComponent(id)}`, esquemaDefinicaoDeCampo, mudancas),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "campos"] }),
  });
}

export function useGravarCampos(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (valores: Record<string, unknown>) =>
      atualizarJson(`/tickets/${encodeURIComponent(ticketId)}/campos`, z.array(esquemaCampoDoChamado), {
        valores,
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/* --------------------------------------------------------------------- relações */

const esquemaResumo = z.object({
  id: z.string().uuid(),
  numero: z.number().int().positive(),
  assunto: z.string(),
  estado: z.string(),
  urgencia: z.string(),
});
const esquemaLigacao = z.object({ relacao_id: z.string().uuid(), chamado: esquemaResumo });
const esquemaRelacoes = z.object({
  pai: esquemaLigacao.nullable(),
  filhos: z.array(esquemaLigacao),
  relacionados: z.array(esquemaLigacao),
  mesclados: z.array(esquemaResumo),
  mesclado_em: esquemaResumo.nullable(),
});

export type RelacoesDoChamado = z.infer<typeof esquemaRelacoes>;
export type ResumoDeChamado = z.infer<typeof esquemaResumo>;
export type PapelDaRelacao = "pai" | "filho" | "relacionado";

export const ROTULO_DO_PAPEL_DA_RELACAO: Record<PapelDaRelacao, string> = {
  pai: "É o chamado pai deste",
  filho: "É um chamado filho deste",
  relacionado: "Está relacionado a este",
};

export function useRelacoes(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "relacoes"],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/${encodeURIComponent(ticketId)}/relacoes`, esquemaRelacoes, { signal }),
    enabled: enabled && Boolean(ticketId),
  });
}

function useAtoDeRelacao<T>(ticketId: string, enviar: (base: string, corpo: T) => Promise<unknown>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: T) => enviar(`/tickets/${encodeURIComponent(ticketId)}`, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

export function useRelacionar(ticketId: string) {
  return useAtoDeRelacao<{ numero: number; papel: PapelDaRelacao }>(ticketId, (base, corpo) =>
    enviarJson(`${base}/relacoes`, esquemaRelacoes, corpo),
  );
}

export function useDesfazerRelacao(ticketId: string) {
  return useAtoDeRelacao<string>(ticketId, (base, relacaoId) =>
    apagarJson(`${base}/relacoes/${encodeURIComponent(relacaoId)}`, esquemaRelacoes),
  );
}

export function useMesclar(ticketId: string) {
  return useAtoDeRelacao<number>(ticketId, (base, numero) =>
    enviarJson(`${base}/mesclar`, esquemaRelacoes, { numero }),
  );
}

export function useDesmesclar(ticketId: string) {
  return useAtoDeRelacao<void>(ticketId, (base) => enviarJson(`${base}/desmesclar`, esquemaRelacoes, {}));
}

export function useClonar(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => enviarJson(`/tickets/${encodeURIComponent(ticketId)}/clonar`, esquemaTicket, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

/* ------------------------------------------------------------ abertura programada */

export const FREQUENCIAS = ["unica", "diaria", "semanal", "mensal"] as const;
export type Frequencia = (typeof FREQUENCIAS)[number];

export const ROTULO_DA_FREQUENCIA: Record<Frequencia, string> = {
  unica: "Uma vez",
  diaria: "Todo dia",
  semanal: "Toda semana",
  mensal: "Todo mês",
};

export const DIAS_DA_SEMANA = ["segunda", "terça", "quarta", "quinta", "sexta", "sábado", "domingo"] as const;

const esquemaAgendamento = z.object({
  id: z.string().uuid(),
  organizacao_id: z.string().uuid(),
  nome: z.string(),
  frequencia: z.enum(FREQUENCIAS),
  hora: z.string(),
  dia_da_semana: z.number().int().min(0).max(6).nullable(),
  dia_do_mes: z.number().int().min(1).max(31).nullable(),
  assunto: z.string(),
  descricao: z.string(),
  urgencia: z.enum(["baixa", "media", "alta", "urgente"]),
  tipo: z.enum(["publico", "interno"]),
  categoria: z.string().nullable(),
  servico: z.string().nullable(),
  solicitante_id: z.string().uuid().nullable(),
  equipe_id: z.string().uuid().nullable(),
  responsavel_id: z.string().uuid().nullable(),
  campos: z.record(z.string(), z.unknown()),
  ativo: z.boolean(),
  proxima_execucao_em: z.string().nullable(),
  ultima_execucao_em: z.string().nullable(),
  criado_em: z.string(),
});

const esquemaExecucao = z.object({
  id: z.string().uuid(),
  previsto_para: z.string(),
  executado_em: z.string(),
  ticket_id: z.string().uuid().nullable(),
  numero_do_ticket: z.number().int().nullable(),
  erro: z.string().nullable(),
});

export type Agendamento = z.infer<typeof esquemaAgendamento>;
export type ExecucaoDeAgendamento = z.infer<typeof esquemaExecucao>;

export interface PedidoDeAgendamento {
  organizacao_id: string;
  nome: string;
  frequencia: Frequencia;
  /** `HH:MM`; ignorado na abertura única (vale o horário de `executar_em`). */
  hora?: string;
  dia_da_semana?: number | null;
  dia_do_mes?: number | null;
  /** Só na abertura única: data e hora locais (`AAAA-MM-DDTHH:MM`), no horário de Brasília. */
  executar_em?: string;
  assunto: string;
  descricao: string;
  urgencia: "baixa" | "media" | "alta" | "urgente";
  tipo?: "publico" | "interno";
  categoria?: string | null;
  servico?: string | null;
  campos?: Record<string, unknown>;
}

/** Frase curta que descreve a agenda: "Toda segunda às 09:00". */
export function descreverAgenda(item: Pick<Agendamento, "frequencia" | "hora" | "dia_da_semana" | "dia_do_mes">): string {
  const hora = item.hora.slice(0, 5);
  if (item.frequencia === "diaria") return `Todo dia às ${hora}`;
  if (item.frequencia === "semanal") {
    const indice = item.dia_da_semana ?? 0;
    const dia = DIAS_DA_SEMANA[indice] ?? "segunda";
    return indice <= 4 ? `Toda ${dia}-feira às ${hora}` : `Todo ${dia} às ${hora}`;
  }
  if (item.frequencia === "mensal") return `Todo dia ${item.dia_do_mes ?? 1} do mês às ${hora}`;
  return `Uma vez, às ${hora}`;
}

/** Acrescenta o fuso de Brasília ao valor de um `datetime-local`, que a API lê como instante. */
export function instanteEmBrasilia(local: string): string {
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(local) ? local : `${local.length === 16 ? `${local}:00` : local}-03:00`;
}

/* Só a lista: as aberturas de uma rotina moram sob a mesma chave, e uma invalidação geral
   refaria a consulta delas logo depois de a rotina ser removida — um 404 que a pessoa veria. */
const CHAVE_DA_AGENDA = [...CHAVE, "agendamentos"] as const;
const invalidarAAgenda = (cliente: ReturnType<typeof useQueryClient>) =>
  cliente.invalidateQueries({ queryKey: CHAVE_DA_AGENDA, exact: true });

export function useAgendamentos() {
  return useQuery({
    queryKey: CHAVE_DA_AGENDA,
    queryFn: ({ signal }) => pedirJson("/tickets/agendamentos", z.array(esquemaAgendamento), { signal }),
  });
}

export function useExecucoesDoAgendamento(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...CHAVE, "agendamentos", id, "execucoes"],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/agendamentos/${encodeURIComponent(id)}/execucoes`, z.array(esquemaExecucao), { signal }),
    enabled,
  });
}

export function useCriarAgendamento() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeAgendamento) => enviarJson("/tickets/agendamentos", esquemaAgendamento, corpo),
    onSuccess: () => void invalidarAAgenda(cliente),
  });
}

export function useAlterarAgendamento() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mudancas }: { id: string; mudancas: Partial<PedidoDeAgendamento> & { ativo?: boolean } }) =>
      editarJson(`/tickets/agendamentos/${encodeURIComponent(id)}`, esquemaAgendamento, mudancas),
    onSuccess: () => void invalidarAAgenda(cliente),
  });
}

export function useRemoverAgendamento() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagar(`/tickets/agendamentos/${encodeURIComponent(id)}`),
    onSuccess: () => void invalidarAAgenda(cliente),
  });
}
